import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError, Plan, Subscription, SubscriptionEvent } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ожидает оплаты', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  trial: { label: 'Пробный период', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  active: { label: 'Активна', color: 'bg-green-100 text-green-700 border-green-200' },
  cancelled: { label: 'Отменена', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  expired: { label: 'Истекла', color: 'bg-red-100 text-red-700 border-red-200' },
  past_due: { label: 'Просрочена', color: 'bg-orange-100 text-orange-700 border-orange-200' },
};

const EVENT_LABELS: Record<string, string> = {
  created: 'Создана',
  trial_started: 'Начат пробный период',
  activated: 'Активирована',
  renewed: 'Продлена',
  upgraded: 'Улучшен тариф',
  downgraded: 'Понижен тариф',
  cancelled: 'Отменена',
  expired: 'Истекла',
  payment_received: 'Оплата получена',
};

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function BillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [history, setHistory] = useState<SubscriptionEvent[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isSystemOwner, setIsSystemOwner] = useState(false);
  const [paying, setPaying] = useState<string | null>(null);
  const [trialing, setTrialing] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState<Record<string, { percent: number; name: string; base_stars: number; amount_stars: number }>>({});
  const [promoError, setPromoError] = useState<string | null>(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sub, ent] = await Promise.all([
        api.getMyBillingSubscription(),
        api.getMyBillingPlan(),
      ]);
      setSubscription(sub.subscription);
      setHistory(sub.history || []);
      setPlans(sub.available_plans || []);
      setIsSystemOwner(!!ent.is_system_owner);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить тарифы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Статус текущей подписки
  const statusInfo = subscription ? (STATUS_LABELS[subscription.status] || STATUS_LABELS.pending) : null;
  const currentPlanId = subscription?.plan_id;

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromoResult({}); setPromoError(null); return; }
    setCheckingPromo(true);
    setPromoError(null);
    try {
      // Проверяем код на всех платных тарифах — вдруг он действует не на все.
      const results: Record<string, { percent: number; name: string; base_stars: number; amount_stars: number }> = {};
      for (const plan of plans) {
        if (plan.id === 'free') continue;
        try {
          const res = await api.applyPromo(plan.id, code);
          if (res.valid) results[plan.id] = res;
        } catch { /* не действует на этот тариф */ }
      }
      if (Object.keys(results).length === 0) {
        setPromoError('Промо-код недействителен или истёк');
        setPromoResult({});
      } else {
        setPromoResult(results);
      }
    } catch {
      setPromoError('Не удалось проверить промо-код. Попробуйте ещё раз.');
    } finally {
      setCheckingPromo(false);
    }
  };

  const handleCancel = async () => {
    if (!subscription) return;
    if (!confirm('Отменить подписку? Доступ сохранится до конца оплаченного периода.')) return;
    setCancelling(true);
    setError(null);
    setMsg(null);
    try {
      await api.cancelMySubscription();
      setMsg('Подписка отменена — доступ сохранится до конца периода.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось отменить подписку.');
    } finally {
      setCancelling(false);
    }
  };

  const handlePay = async (planId: string) => {
    setPaying(planId);
    setError(null);
    setMsg(null);
    try {
      const invoice = await api.createInvoice(planId, promoCode.trim() || undefined);
      const tg = window.Telegram?.WebApp;
      if (tg && typeof tg.openInvoice === 'function') {
        tg.openInvoice(invoice.invoice_link, async (status: string) => {
          if (status === 'paid') {
            setMsg('⭐ Оплата получена! Активируем подписку…');
            await load();
          } else if (status === 'failed') {
            setMsg('Оплата не прошла. Попробуйте ещё раз.');
          } else {
            setMsg('Оплата отменена — вы можете вернуться в любой момент.');
          }
        });
      } else {
        // Fallback: браузер вне Telegram
        window.open(invoice.invoice_link, '_blank');
        setMsg('Откройте ссылку в Telegram, чтобы оплатить');
      }
    } catch (e) {
      setError(e instanceof ApiError && e.status === 503
        ? 'Оплата временно недоступна — бот не настроен. Попробуйте позже.'
        : 'Не удалось создать платёж. Попробуйте ещё раз.');
    } finally {
      setPaying(null);
    }
  };

  const handleTrial = async (planId: string) => {
    setTrialing(planId);
    setError(null);
    setMsg(null);
    try {
      const res = await api.startMyTrial(planId);
      setMsg(res.created
        ? '🎉 Пробный период активирован!'
        : 'Пробный период для этого тарифа уже был использован.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? 'Пробный период уже был использован.' : 'Не удалось активировать пробный период.');
    } finally {
      setTrialing(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[60vh]">
        <div className="text-center animate-fade-in">
          <div className="w-10 h-10 rounded-xl bg-[var(--tg-theme-button-color)] mx-auto mb-3 animate-pulse" />
          <p className="text-sm text-[var(--tg-theme-hint-color)]">Загрузка тарифов…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 animate-fade-in">
      {/* ── Назад ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Назад
        </button>
      </div>

      {/* ── Заголовок ─────────────────────────────────────────────── */}
      <div className="tg-card flex items-center gap-3 py-4 px-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--tg-theme-button-color)] to-purple-600 flex items-center justify-center text-lg text-white shrink-0">
          ⭐
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-[var(--tg-theme-text-color)]">Тариф и подписка</h2>
          <p className="text-[11px] text-[var(--tg-theme-hint-color)]">
            {user?.role === 'owner_school'
              ? 'Тариф школы распространяется на всю команду'
              : 'Оплата проходит через Telegram Stars'}
          </p>
        </div>
      </div>

      {isSystemOwner && (
        <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-700">
          👑 Вы владелец EduPulse — все платные функции доступны без ограничений.
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}
      {msg && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {msg}
        </div>
      )}

      {/* ── Текущая подписка ──────────────────────────────────────── */}
      {subscription && (
        <div className="tg-card">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
            <span>📋</span> Ваша подписка
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[var(--tg-theme-hint-color)]">Тариф</span>
              <span className="font-semibold text-[var(--tg-theme-text-color)] capitalize">
                {subscription.plan_id || 'free'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[var(--tg-theme-hint-color)]">Статус</span>
              {statusInfo && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              )}
            </div>
            {subscription.start_date && (
              <div className="flex items-center justify-between">
                <span className="text-[var(--tg-theme-hint-color)]">Начало</span>
                <span className="text-[var(--tg-theme-text-color)]">{fmtDate(subscription.start_date)}</span>
              </div>
            )}
            {subscription.end_date && (
              <div className="flex items-center justify-between">
                <span className="text-[var(--tg-theme-hint-color)]">Действует до</span>
                <span className="text-[var(--tg-theme-text-color)]">{fmtDate(subscription.end_date)}</span>
              </div>
            )}
            {subscription.payment_reference && (
              <div className="flex items-center justify-between">
                <span className="text-[var(--tg-theme-hint-color)]">Платёж</span>
                <span className="font-mono text-[11px] text-[var(--tg-theme-text-color)] opacity-70">
                  {subscription.payment_reference}
                </span>
              </div>
            )}
          </div>
          {(subscription.status === 'active' || subscription.status === 'trial') && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="mt-3 w-full py-2 rounded-xl border border-red-200 text-red-500 text-xs font-semibold transition-all hover:opacity-80 active:scale-[0.98] disabled:opacity-50"
            >
              {cancelling ? 'Отменяю…' : 'Отменить подписку'}
            </button>
          )}
        </div>
      )}

      {/* ── Промо-код ────────────────────────────────────────────── */}
      <div className="tg-card p-3 space-y-2">
        <label className="text-[11px] font-medium text-[var(--tg-theme-hint-color)]">Промо-код (если есть)</label>
        <div className="flex gap-2">
          <input
            value={promoCode}
            onChange={e => { setPromoCode(e.target.value); setPromoError(null); }}
            placeholder="Например: WELCOME20"
            className="flex-1 rounded-lg px-3 py-2 text-sm bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)] outline-none border border-[var(--tg-theme-section-separator-color)] focus:border-[var(--tg-theme-button-color)]"
          />
          <button
            onClick={handleApplyPromo}
            disabled={checkingPromo}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] disabled:opacity-50 shrink-0"
          >
            {checkingPromo ? '…' : 'Применить'}
          </button>
        </div>
        {promoError && (
          <p className="text-xs text-red-500">{promoError}</p>
        )}
        {Object.keys(promoResult).length > 0 && (
          <p className="text-xs text-green-600">
            🏷️ Код применён: скидка {promoResult[Object.keys(promoResult)[0]].percent}% на выбранные тарифы
          </p>
        )}
      </div>

      {/* ── Тарифы ────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] flex items-center gap-2 px-1">
          <span>💎</span> Выберите тариф
        </h3>
        {plans.length === 0 && (
          <p className="text-xs text-[var(--tg-theme-hint-color)] px-1">
            Тарифы скоро появятся.
          </p>
        )}
        {plans.map(plan => {
          const isCurrent = plan.id === currentPlanId;
          const stars = Number(plan.price_stars) || 0;
          const trialDays = Number(plan.trial_days) || 0;
          const featureEntries = Object.entries(plan.features || {});
          const hasTrial = trialDays > 0 && plan.id !== 'free';
          return (
            <div
              key={plan.id}
              className={`tg-card relative overflow-hidden ${isCurrent ? 'ring-2 ring-[var(--tg-theme-button-color)]' : ''}`}
            >
              {isCurrent && (
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] text-[10px] font-semibold">
                  Ваш тариф
                </div>
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-bold text-[var(--tg-theme-text-color)] capitalize">
                    {plan.name}
                  </h4>
                  {plan.description && (
                    <p className="text-[11px] text-[var(--tg-theme-hint-color)] mt-0.5 leading-relaxed">
                      {plan.description}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {(() => {
                    const promo = promoResult[plan.id];
                    const auto = (plan as Plan & { discount?: { percent: number; name: string; base_stars: number; amount_stars: number } }).discount;
                    const discount = promo || auto;
                    const discounted = discount ? discount.amount_stars : null;
                    const showStars = discounted != null ? discounted : stars;
                    return (
                      <>
                        <div className="text-lg font-bold text-[var(--tg-theme-text-color)]">
                          {stars > 0 ? (
                            <span>⭐ {showStars}</span>
                          ) : (
                            <span className="text-[var(--tg-theme-hint-color)]">Бесплатно</span>
                          )}
                        </div>
                        {discount && stars > 0 && (
                          <div className="text-[11px]">
                            <span className="line-through text-[var(--tg-theme-hint-color)]">⭐ {discount.base_stars}</span>
                            <span className="text-green-600 font-semibold ml-1">−{discount.percent}%</span>
                          </div>
                        )}
                        {stars > 0 && (
                          <div className="text-[10px] text-[var(--tg-theme-hint-color)]">за {plan.billing_period === 'yearly' ? 'год' : 'месяц'}</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {featureEntries.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {featureEntries
                    .filter(([, enabled]) => enabled)
                    .map(([key]) => (
                      <li key={key} className="flex items-center gap-2 text-xs text-[var(--tg-theme-text-color)]">
                        <span className="text-green-500">✓</span>
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      </li>
                    ))}
                </ul>
              )}

              {plan.id !== 'free' && (
                <div className="mt-3 flex gap-2">
                  {!isCurrent && (
                    <>
                      <button
                        onClick={() => handlePay(plan.id)}
                        disabled={paying === plan.id || trialing === plan.id}
                        className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[var(--tg-theme-button-color)] to-purple-600 text-[var(--tg-theme-button-text-color)] font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-1.5"
                      >
                        {paying === plan.id ? (
                          <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                        ) : (
                          <>⭐ Оплатить</>
                        )}
                      </button>
                      {hasTrial && (
                        <button
                          onClick={() => handleTrial(plan.id)}
                          disabled={paying === plan.id || trialing === plan.id}
                          className="px-4 py-2.5 rounded-xl border border-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-color)] font-semibold text-sm transition-all hover:opacity-80 active:scale-[0.98] disabled:opacity-60 shrink-0"
                        >
                          {trialing === plan.id ? '…' : `Попробовать ${trialDays} дн.`}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── История ───────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="tg-card">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
            <span>🕘</span> История подписок
          </h3>
          <div className="space-y-2">
            {history.slice(0, 10).map(ev => (
              <div key={ev.id} className="flex items-start gap-2 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--tg-theme-button-color)] mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-[var(--tg-theme-text-color)]">
                    {EVENT_LABELS[ev.event_type] || ev.event_type}
                  </span>
                  {ev.detail && (
                    <span className="text-[var(--tg-theme-hint-color)]"> — {ev.detail}</span>
                  )}
                </div>
                <span className="text-[10px] text-[var(--tg-theme-hint-color)] shrink-0">
                  {fmtDate(ev.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-2 flex items-center gap-2">
          <span>❓</span> Как это работает
        </h3>
        <ul className="text-xs text-[var(--tg-theme-hint-color)] space-y-1.5 leading-relaxed">
          <li>1. Нажмите «Оплатить» — откроется окно оплаты Telegram Stars.</li>
          <li>2. После оплаты подписка активируется автоматически.</li>
          <li>3. Продление — повторной оплатой: когда срок истечёт, нажмите «Оплатить» снова. Telegram Stars не поддерживает автоматическое списание.</li>
          <li>4. Отменить подписку можно в любой момент — доступ сохранится до конца оплаченного периода.</li>
          <li>5. За 3 дня до истечения бот напомнит вам об этом в Telegram.</li>
        </ul>
      </div>
    </div>
  );
}
